import { useState, useEffect } from 'react';
import KakaoTalkButton from '@/components/feature/KakaoTalkButton';

const slides = [
  {
    img: 'https://readdy.ai/api/search-image?query=Korean%20professional%20caregiver%20in%20light%20green%20uniform%20gently%20holding%20elderly%20patient%20hand%20in%20bright%20hospital%20room%2C%20warm%20natural%20light%20streaming%20through%20window%2C%20clean%20white%20walls%2C%20soft%20beige%20tones%2C%20compassionate%20care%20scene%2C%20photorealistic%2C%20high%20quality&width=900&height=700&seq=kc_hero1&orientation=portrait',
    tag: '가족간병 전문 서비스',
    title: '가족간병 등록부터\n보험 청구까지',
    sub: '전국 어디서든 가족간병 등록부터\n간병보험 청구 서류까지 도와드립니다.',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=Korean%20elderly%20patient%20smiling%20warmly%20with%20professional%20caregiver%20in%20comfortable%20hospital%20room%2C%20warm%20sunlight%2C%20clean%20modern%20interior%2C%20soft%20green%20and%20white%20tones%2C%20hopeful%20atmosphere%2C%20photorealistic%2C%20high%20quality&width=900&height=700&seq=kc_hero2&orientation=portrait',
    tag: '보험 청구 서류 지원',
    title: '보험 청구에 필요한\n서류를 제공합니다',
    sub: '간병비 청구에 필요한 서류를 안내하고\n발급해 드립니다. 청구는 직접 보험사에 접수하세요.',
  },
  {
    img: 'https://readdy.ai/api/search-image?query=Korean%20professional%20care%20team%20in%20matching%20green%20uniforms%20standing%20together%20in%20bright%20modern%20office%2C%20confident%20smiling%20expressions%2C%20clean%20white%20background%2C%20trust%20and%20professionalism%2C%20photorealistic%2C%20high%20quality&width=900&height=700&seq=kc_hero3&orientation=portrait',
    tag: '24시간 연중무휴',
    title: '언제든지 연락하세요\n24시간 상담 가능',
    sub: '가족간병은 전국 어디서든 가능합니다.\n거주 지역과 상관없이 전화·온라인으로 상담하세요.',
  },
];

export default function HeroSection() {
  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimating(true);
      setTimeout(() => {
        setCurrent((c) => (c + 1) % slides.length);
        setAnimating(false);
      }, 400);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (i: number) => {
    setAnimating(true);
    setTimeout(() => { setCurrent(i); setAnimating(false); }, 300);
  };

  const slide = slides[current];

  return (
    <section className="relative w-full min-h-screen flex items-stretch overflow-hidden" style={{ backgroundColor: '#0d2b24' }}>
      {/* Left: Text Panel */}
      <div className="relative z-10 flex flex-col justify-center w-full md:w-1/2 px-8 md:px-16 py-24 md:py-0">
        {/* Decorative line */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-8 h-px" style={{ backgroundColor: '#2E7D6B' }}></div>
          <span
            className="text-xs font-semibold tracking-widest uppercase px-3 py-1 rounded-full"
            style={{ backgroundColor: 'rgba(46,125,107,0.2)', color: '#6fcfb8' }}
          >
            {slide.tag}
          </span>
        </div>

        {/* Title */}
        <h1
          className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 whitespace-pre-line transition-opacity duration-300"
          style={{ opacity: animating ? 0 : 1 }}
        >
          {slide.title}
        </h1>

        {/* Sub */}
        <p
          className="text-sm md:text-base leading-relaxed mb-10 whitespace-pre-line transition-opacity duration-300"
          style={{ color: 'rgba(255,255,255,0.6)', opacity: animating ? 0 : 1 }}
        >
          {slide.sub}
        </p>

        {/* CTA */}
        <div className="flex flex-wrap gap-3 mb-12">
          <a
            href="/register"
            className="px-7 py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer transition-all hover:opacity-90"
            style={{ backgroundColor: '#2E7D6B' }}
          >
            <i className="ri-user-add-line mr-2"></i>가족간병 등록하기
          </a>
          <a
            href="/caregiver-job"
            className="px-7 py-3.5 rounded-xl text-sm font-bold text-white whitespace-nowrap cursor-pointer transition-all hover:opacity-90"
            style={{ backgroundColor: '#EA8C00' }}
          >
            <i className="ri-briefcase-line mr-2"></i>간병인 일자리
          </a>
          <a
            href="tel:15668654"
            className="px-7 py-3.5 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer transition-all"
            style={{ border: '1px solid rgba(255,255,255,0.25)', color: 'rgba(255,255,255,0.85)' }}
          >
            <i className="ri-phone-line mr-2"></i>1566-8654
          </a>
          <KakaoTalkButton
            className="px-7 py-3.5 rounded-xl text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:opacity-90"
            style={{ backgroundColor: '#FEE500', color: '#391B1B' }}
            ariaLabel="카톡 상담"
          >
            <i className="ri-kakao-talk-fill mr-2"></i>카톡 상담
          </KakaoTalkButton>
        </div>

        {/* Slide controls */}
        <div className="flex items-center gap-4">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="cursor-pointer transition-all"
              style={{
                width: i === current ? '32px' : '8px',
                height: '4px',
                borderRadius: '2px',
                backgroundColor: i === current ? '#2E7D6B' : 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
          <span className="text-xs ml-2" style={{ color: 'rgba(255,255,255,0.3)' }}>
            {String(current + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}
          </span>
        </div>

        {/* Bottom info strip */}
        <div
          className="absolute bottom-0 left-0 right-0 md:right-auto flex items-center gap-6 px-8 md:px-16 py-4"
          style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
        >
          <div className="flex items-center gap-2">
            <i className="ri-time-line text-xs" style={{ color: '#6fcfb8' }}></i>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>09:00 ~ 23:00 연중무휴</span>
          </div>
          <div className="flex items-center gap-2">
            <i className="ri-map-pin-2-line text-xs" style={{ color: '#6fcfb8' }}></i>
            <span className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>전국 서비스 가능</span>
          </div>
        </div>
      </div>

      {/* Right: Image Panel */}
      <div className="hidden md:block absolute right-0 top-0 bottom-0 w-1/2">
        {slides.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-700"
            style={{ opacity: i === current ? 1 : 0 }}
          >
            <img src={s.img} alt={s.title} className="w-full h-full object-cover object-top" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, #0d2b24 0%, transparent 30%)' }}></div>
          </div>
        ))}
        {/* Floating badge */}
        <div
          className="absolute bottom-12 left-8 rounded-2xl px-5 py-4 z-10"
          style={{ backgroundColor: 'rgba(46,125,107,0.9)', backdropFilter: 'blur(8px)' }}
        >
          <p className="text-white text-xs font-semibold mb-1">코리아케어러스</p>
          <p className="text-white/70 text-xs">가족간병 등록부터 서류 발급까지</p>
          <div className="flex items-center gap-1 mt-2">
            {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-yellow-400 text-xs"></i>)}
            <span className="text-white/60 text-xs ml-1">5.0</span>
          </div>
        </div>
      </div>
    </section>
  );
}